import React, { FC, useMemo } from "react";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import Select from "@/components/Select/Select";
import { useAppTheme } from "@/utils/theme";
import { useCategoryByType } from "@/hooks/useCategories";

const EXTERNAL_PROVIDER_CATEGORY_TYPE = "EXTERNAL_PROVIDERS";

export type Filters = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  status: string;
  categoryId: string;
};

type Props = {
  filtersState: [Filters, React.Dispatch<React.SetStateAction<Filters>>];
};

const pickCategoryLabel = (
  langs: { name: string; language: string }[] | undefined
) => {
  if (!langs || langs.length === 0) return "";
  const en = langs.find((l) => l.language?.toLowerCase().startsWith("en"));
  return (en || langs[0]).name;
};

const ExternalProviderFilters: FC<Props> = ({ filtersState }) => {
  const theme = useAppTheme();
  const [filters, setFilters] = filtersState;

  const { category, isLoadingCategory } = useCategoryByType({
    type: EXTERNAL_PROVIDER_CATEGORY_TYPE,
    status: "ACTIVE",
  });

  const categoryOptions = useMemo(
    () =>
      (category?.categories || []).map((c) => ({
        key: c._id,
        value: pickCategoryLabel(c.languages) || c._id,
      })),
    [category]
  );

  return (
    <Box
      sx={{
        display: "flex",
        gap: "0.5rem",
        width: "100%",
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "row",
        },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.surface.light2,
          px: 2,
          width: {
            xs: "100%",
            sm: "100%",
          },
        }}
        className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
      >
        <InputBase
          className="flex-1"
          placeholder="Search by name"
          inputProps={{ "aria-label": "search" }}
          onChange={(e) => {
            setFilters((filters) => ({
              ...filters,
              search: e.target.value,
              pageNumber: 0,
            }));
          }}
          value={filters.search}
        />
        <SearchIcon className="text-icon-primary" />
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: "0.5rem",
          width: {
            xs: "100%",
            sm: "100%",
          },
          flexDirection: {
            xs: "column",
            sm: "row",
            md: "row",
          },
        }}
      >
        <Select
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              status: value as string,
              pageNumber: 0,
            }));
          }}
          data={[
            { key: "ACTIVE", value: "Active" },
            { key: "INACTIVE", value: "Inactive" },
          ]}
          value={filters.status}
          label="Status"
          id="provider-status-filter"
          maxWidth={220}
          showClearOption
          fullWidth
        />
        <Select
          handleChange={(value) => {
            setFilters((filters) => ({
              ...filters,
              categoryId: value as string,
              pageNumber: 0,
            }));
          }}
          data={categoryOptions}
          value={filters.categoryId}
          label="Category"
          id="provider-category-filter"
          maxWidth={240}
          isLoading={isLoadingCategory}
          showClearOption
          fullWidth
        />
      </Box>
    </Box>
  );
};

export default ExternalProviderFilters;
